import { useRouter } from 'next/router';
import React from 'react';
import { useTranslationContext } from '@/context/translation';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
	useTranslationWithLocale,
	useVideoUploadDropzone,
} from '@/hooks';
import { getCurrentLandingPath, isPageLocalized } from '@/helpers';
import { reviewsLanding } from '@/data';
import type { LandingConfigItem } from '@/types';
import { AMPLITUDE_EVENTS, DEFAULT_LOCALE } from '@/constants';
import { Reviews } from '../reusableSections';
import { Benefits } from './Benefits';
import { FAQ } from './FAQ';
import {
	buildSpeechToTextCookie,
	HeroSpeechToText,
	SPEECH_TO_TEXT_COOKIE_OPTIONS,
} from './HeroSpeechToText';
import { HowItWorks } from './HowItWorks';
import { ToolsWidget } from './ToolsWidget';
import { TryLift } from './TryLift';
import { WhyLift } from './WhyLift';

interface Props {
	data: LandingConfigItem;
	funnelLinkService?: string;
}

/**
 * Landing whose hero takes an audio/video file or a YouTube link and hands it
 * to the Speech to Text funnel through a single JSON cookie. Section CTAs go
 * straight to the funnel, so `withDropZone` is expected to be false.
 */
export const SpeechToTextLandingTemplate: React.FC<Props> = ({
	data,
	funnelLinkService,
}) => {
	const router = useRouter();
	const { locale, translations } = useTranslationContext();

	const currentPath = getCurrentLandingPath(router);
	const isLocalized = isPageLocalized(currentPath);
	const reviewsLocale = isLocalized ? locale : DEFAULT_LOCALE;

	const { t } = useTranslationWithLocale(
		data.translationLocale,
		locale,
		translations?.[data.translationLocale] as
			| Record<string, unknown>
			| undefined,
	);
	const { t: tShared } = useTranslationWithLocale('sharedLandingData', locale);
	const { t: tReviews } = useTranslationWithLocale(
		'landing-reviews',
		reviewsLocale,
		translations?.['landing-reviews'] as Record<string, unknown> | undefined,
	);

	const { dropzoneOptions, isUploading, progress } = useVideoUploadDropzone({
		galleryContext: data.analytics.galleryContext,
		funnelLinkService,
		acceptAudio: true,
		mediaCookieKey: data.mediaCookieKey,
		serializeMediaCookie: viewUrl => buildSpeechToTextCookie('file', viewUrl),
		mediaCookieOptions: SPEECH_TO_TEXT_COOKIE_OPTIONS,
	});

	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		data.analytics.scrolledPercentName,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: data.analytics.eventPropertyType },
	);
	useLandingShownEvent({ type: data.analytics.eventPropertyType });

	if (data.hero.type !== 'speechToText') return null;

	return (
		<>
			<HeroSpeechToText
				data={data.hero}
				t={t}
				dropzoneOptions={dropzoneOptions}
				isLoading={isUploading}
				uploadPercentage={progress.percentage}
				funnelLinkService={funnelLinkService}
			/>

			{data.howItWork && (
				<HowItWorks
					data={data.howItWork}
					t={t}
					dropzoneOptions={dropzoneOptions}
					withDropzone={data.withDropZone}
					funnelLinkService={funnelLinkService}
					useEmailFlow={false}
				/>
			)}

			<Reviews t={tReviews} data={reviewsLanding} />

			{data.benefits && (
				<Benefits
					data={data.benefits}
					t={t}
					dropzoneOptions={dropzoneOptions}
					withDropzone={data.withDropZone}
					funnelLinkService={funnelLinkService}
					useEmailFlow={false}
				/>
			)}

			{data.faq && (
				<FAQ
					data={data.faq}
					t={t}
					funnelLinkService={funnelLinkService}
					withDropzone={data.withDropZone}
					dropzoneOptions={dropzoneOptions}
					useEmailFlow={false}
				/>
			)}

			{data.whyLift && (
				<WhyLift
					data={data.whyLift}
					t={t}
					dropzoneOptions={dropzoneOptions}
					withDropzone={data.withDropZone}
					funnelLinkService={funnelLinkService}
					useEmailFlow={false}
				/>
			)}

			{!data.hideToolsWidget && data.explore && (
				<ToolsWidget data={data.explore} t={tShared} />
			)}

			{data.tryLift && <TryLift data={data.tryLift} t={tShared} />}
		</>
	);
};
